/**
 * The backend rejects a request in two different shapes, and only one of them has a `message`.
 *
 * 1. Controller-level rejections use the house envelope:
 *      { message: "Polling unit result already exists.", isSuccessful: false, statusCode: 400 }
 *
 * 2. Model-binding/validation failures use ASP.NET ProblemDetails, which has NO `message`:
 *      { type: "...", title: "One or more validation errors occurred.", status: 400,
 *        errors: { Ward: ["The Ward field is required."], Zone: [...] } }
 *
 * Shape 2 is what a V2 result submission returns when a required nested object is missing —
 * i.e. precisely the failure an agent most needs explained. Reading only `message` drops it on
 * the floor and reports "Something went wrong".
 */

/** Longest message we will surface verbatim; beyond this the field list is more useful. */
const MAX_DETAIL_LENGTH = 240;

const firstString = (value: unknown): string => {
	if (typeof value === 'string' && value.trim()) return value.trim();
	if (Array.isArray(value)) {
		for (const entry of value) {
			const found = firstString(entry);
			if (found) return found;
		}
	}
	return '';
};

/**
 * Pull a human-readable reason out of an error response body, whichever shape it arrived in.
 * Returns '' when the body carries nothing worth showing, so callers can fall back to their
 * own copy rather than rendering an empty alert.
 */
export const extractApiErrorMessage = (body: unknown): string => {
	if (!body || typeof body !== 'object') return firstString(body);

	const record = body as Record<string, unknown>;

	// Shape 1 — the house envelope.
	const message = firstString(record.message);
	if (message) return message;

	// Shape 2 — ProblemDetails. Prefer the per-field messages over the generic title, which is
	// always "One or more validation errors occurred." and tells the agent nothing.
	const errors = record.errors;
	if (errors && typeof errors === 'object') {
		const fields = Object.entries(errors as Record<string, unknown>)
			.map(([field, messages]) => ({ field, text: firstString(messages) }))
			.filter((entry) => entry.text);

		if (fields.length === 1) return fields[0].text;

		if (fields.length > 1) {
			const joined = fields.map((entry) => entry.text).join(' ');
			// Several field errors at once usually means a whole section of the payload is
			// absent; naming the fields is more actionable than a wall of sentences.
			return joined.length <= MAX_DETAIL_LENGTH ? joined : `Missing or invalid: ${fields.map((e) => e.field).join(', ')}.`;
		}
	}

	return firstString(record.detail) || firstString(record.title);
};
